/**
 * Analyze “insights” normalization.
 *
 * Prefer a single API shape long-term, e.g. `{ "insights": "..." }` or `{ "insights": ["..."] }`.
 * Until then, these helpers tolerate legacy / inconsistent payloads from runQuery, getInsights,
 * and persisted message rows.
 */

const INSIGHT_PAYLOAD_KEYS = [
  "insights",
  "Insights",
  "insight",
  "Insight",
  "insightSummary",
  "insight_summary",
  "InsightSummary",
  "insightText",
  "insightsText",
  "InsightText",
  "insightNarrative",
  "narrative",
  "Narrative",
  "llmInsights",
  "aiInsights",
  "keyInsights",
  "KeyInsights",
  "summary",
];

const parseMaybeJson = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const normalizeObjectCandidate = (value) => {
  if (value == null) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value;
  return parseMaybeJson(value);
};

const FAQ_PAYLOAD_KEYS = [
  "faqs",
  "FAQs",
  "Faqs",
  "faq",
  "Faq",
  "suggestedQuestions",
  "suggested_questions",
  "SuggestedQuestions",
  "followUpQuestions",
  "follow_up_questions",
];

const PARQUET_NAME_KEYS = [
  "parquet_file_name",
  "parquetFileName",
  "ParquetFileName",
  "file_name",
  "fileName",
  "filename",
];

const unwrapNamedArray = (value, keys) => {
  if (Array.isArray(value) && value.length > 0) return value;
  const obj = normalizeObjectCandidate(value);
  if (!obj) return null;
  for (const key of keys) {
    const nested = obj[key];
    if (Array.isArray(nested) && nested.length > 0) return nested;
    const nestedObj = normalizeObjectCandidate(nested);
    if (nestedObj && nested !== obj) {
      const deeper = nestedObj[key];
      if (Array.isArray(deeper) && deeper.length > 0) return deeper;
    }
  }
  return null;
};

const pickInsightsFromObject = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  for (const key of INSIGHT_PAYLOAD_KEYS) {
    const value = obj[key];
    if (value == null) continue;
    if (typeof value === "string" && value.trim()) return value.trim();
    const list = unwrapNamedArray(value, INSIGHT_PAYLOAD_KEYS);
    if (list) return list;
  }
  return unwrapNamedArray(obj, INSIGHT_PAYLOAD_KEYS);
};

const pickFaqsFromObject = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  for (const key of FAQ_PAYLOAD_KEYS) {
    const value = obj[key];
    if (value == null) continue;
    if (typeof value === "string" && value.trim()) {
      const parsed = parseMaybeJson(value);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      const lines = value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (lines.length) return lines;
    }
    const list = unwrapNamedArray(value, FAQ_PAYLOAD_KEYS);
    if (list) return list;
  }
  return unwrapNamedArray(obj, FAQ_PAYLOAD_KEYS);
};

const walkAnalyzeCandidates = (raw, picker) => {
  if (raw == null || typeof raw !== "object") return null;

  const candidates = [
    raw,
    raw.data,
    raw.body,
    raw.payload,
    raw.result,
    raw.analyzeResponse,
  ];
  for (const c of candidates) {
    const obj = normalizeObjectCandidate(c);
    if (!obj) continue;
    const picked = picker(obj);
    if (picked != null) return picked;
    const nestedAr = normalizeObjectCandidate(obj.analyzeResponse);
    if (nestedAr) {
      const nestedPick = picker(nestedAr);
      if (nestedPick != null) return nestedPick;
    }
  }

  const media = raw.contentMedia ?? raw.ContentMedia;
  if (typeof media === "string" && /^\s*[\[{]/.test(media)) {
    try {
      const parsed = JSON.parse(media);
      const picked = picker(parsed);
      if (picked != null) return picked;
      const nested = picker(normalizeObjectCandidate(parsed?.analyzeResponse) || {});
      if (nested != null) return nested;
    } catch {
      // ignore
    }
  }

  return null;
};

const insightObjectToItem = (item) => {
  if (!item || typeof item !== "object") return null;
  const title = String(item.title ?? item.Title ?? "").trim();
  const description = String(
    item.description ??
      item.Description ??
      item.text ??
      item.insight ??
      item.content ??
      item.summary ??
      (typeof item.message === "string" ? item.message : ""),
  ).trim();
  if (!title && !description) return null;
  return { title, description };
};

/**
 * Reduces arbitrary API / DB shapes to a single string, string array, or null.
 * Use this at service boundaries and when mapping persisted messages to meta.
 */
export function parseInsightsPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t || null;
  }
  if (Array.isArray(raw)) {
    return raw.length ? raw : null;
  }
  if (typeof raw !== "object") return null;

  return walkAnalyzeCandidates(raw, pickInsightsFromObject);
}

/** Suggested follow-up questions from analyzeResponse.faqs (or aliases). */
export function parseFaqs(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return String(
            item.question ?? item.text ?? item.prompt ?? item.faq ?? "",
          ).trim();
        }
        return String(item ?? "").trim();
      })
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    const parsed = parseMaybeJson(raw);
    if (Array.isArray(parsed)) return parseFaqs(parsed);
    return raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }
  if (typeof raw !== "object") return [];

  const picked = walkAnalyzeCandidates(raw, pickFaqsFromObject);
  return parseFaqs(picked ?? []);
}

export function extractParquetFileName(raw) {
  if (!raw || typeof raw !== "object") return "";

  const fromObj = (obj) => {
    if (!obj || typeof obj !== "object") return "";
    for (const key of PARQUET_NAME_KEYS) {
      const value = String(obj[key] ?? "").trim();
      if (value) return value;
    }
    return "";
  };

  const direct = fromObj(raw) || fromObj(normalizeObjectCandidate(raw.analyzeResponse));
  if (direct) return direct;

  const walked = walkAnalyzeCandidates(raw, (obj) => {
    const name = fromObj(obj);
    return name || null;
  });
  return String(walked ?? "").trim();
}

export function extractAnalyzeDisplayMeta(raw) {
  return {
    insights: parseInsightsPayload(raw),
    faqs: parseFaqs(raw),
    parquetFileName: extractParquetFileName(raw),
  };
}

export function hasDisplayableInsights(value) {
  return toInsightItems(value).length > 0;
}

/**
 * Maps analyzeResponse.insights into `{ title, description }` rows for the chat UI.
 */
export function toInsightItems(value) {
  if (value == null || value === "") return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (item == null) return [];
      if (typeof item === "string") {
        const t = item.trim();
        return t ? [{ title: "", description: t }] : [];
      }
      if (typeof item === "object") {
        const mapped = insightObjectToItem(item);
        return mapped ? [mapped] : [];
      }
      const t = String(item).trim();
      return t ? [{ title: "", description: t }] : [];
    });
  }

  if (typeof value === "string") {
    const parsed = parseMaybeJson(value);
    if (parsed != null) return toInsightItems(parsed);
    return value
      .split(/(?:\r?\n)+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((description) => ({ title: "", description }));
  }

  if (typeof value === "object") {
    const nestedList = unwrapNamedArray(value, INSIGHT_PAYLOAD_KEYS);
    if (nestedList) return toInsightItems(nestedList);
    const parsed = parseInsightsPayload(value);
    if (parsed != null && parsed !== value) return toInsightItems(parsed);
    const mapped = insightObjectToItem(value);
    if (mapped) return [mapped];
  }

  return [];
}

/** Stable id for chat rows returned from the messages API. */
export function getPersistedMessageId(msg) {
  if (!msg || typeof msg !== "object") return null;
  return (
    msg.messagesId ??
    msg.messageId ??
    msg.MessagesId ??
    msg.MessageId ??
    msg.id ??
    null
  );
}

/**
 * Turns meta.insights (any supported shape) into non-empty lines for the UI.
 */
export function insightsToDisplayLines(value) {
  if (value == null || value === "") return [];

  if (typeof value === "string") {
    return value
      .split(/(?:\r?\n)+/)
      .map((p) => p.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value
      .flatMap((item) => {
        if (item == null) return [];
        if (typeof item === "string") {
          const t = item.trim();
          return t ? [t] : [];
        }
        if (typeof item === "object") {
          const mapped = insightObjectToItem(item);
          if (mapped) {
            return [
              mapped.title && mapped.description
                ? `${mapped.title}: ${mapped.description}`
                : mapped.title || mapped.description,
            ];
          }
          return insightsToDisplayLines(item);
        }
        const t = String(item).trim();
        return t ? [t] : [];
      })
      .filter(Boolean);
  }

  if (typeof value === "object") {
    const parsed = parseInsightsPayload(value);
    if (parsed != null) {
      return insightsToDisplayLines(parsed);
    }
    return [];
  }

  const single = String(value).trim();
  return single ? [single] : [];
}
